import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import BarcodeDisplay from './components/BarcodeDisplay';
import BarcodeGenerationForm from './components/BarcodeGenerationForm';
import BulkGenerationModal from './components/BulkGenerationModal';
import BarcodeHistoryList from './components/BarcodeHistoryList';

const MemberBarcodeGenerator = () => {
  const { user, currentTenant, isGymOwner, isStaff, isTrainer } = useAuth();
  const [activeTab, setActiveTab] = useState('my-barcode');
  const [userBarcode, setUserBarcode] = useState(null);
  const [allBarcodes, setAllBarcodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [error, setError] = useState('');

  const isStaffMember = isGymOwner || isStaff || isTrainer;

  useEffect(() => {
    if (currentTenant?.id && user?.id) {
      fetchUserBarcode();
      if (isStaffMember) {
        fetchAllBarcodes();
      }
    }
  }, [currentTenant?.id, user?.id, isStaffMember]);

  const fetchUserBarcode = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('member_barcodes')
        ?.select('*')
        ?.eq('tenant_id', currentTenant?.id)
        ?.eq('user_id', user?.id)
        ?.single();

      if (error && error?.code !== 'PGRST116') throw error;
      setUserBarcode(data);
    } catch (error) {
      console.error('Error fetching user barcode:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllBarcodes = async () => {
    try {
      const { data, error } = await supabase
        ?.from('member_barcodes')
        ?.select(`
          *,
          user_profiles!member_barcodes_user_id_fkey(full_name, email, avatar_url),
          tenant_members!inner(role, membership_plan, is_active)
        `)
        ?.eq('tenant_id', currentTenant?.id)
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      setAllBarcodes(data || []);
    } catch (error) {
      console.error('Error fetching all barcodes:', error);
    }
  };

  const handleGenerateBarcode = async (userId, barcodeType = 'qr_code') => {
    setLoading(true);
    setError('');

    try {
      const targetUserId = userId || user?.id;
      const barcodeString = `GYM-${currentTenant?.id?.substring(0, 8)}-${targetUserId?.substring(0, 8)}-${Date.now()}`;

      const { data, error } = await supabase
        ?.from('member_barcodes')
        ?.insert({
          tenant_id: currentTenant?.id,
          user_id: targetUserId,
          barcode_data: barcodeString,
          barcode_type: barcodeType,
          is_active: true
        })
        ?.select()
        ?.single();

      if (error) throw error;

      if (targetUserId === user?.id) {
        setUserBarcode(data);
      }

      if (isStaffMember) {
        fetchAllBarcodes();
      }

      return data;
    } catch (error) {
      console.error('Error generating barcode:', error);
      setError('Failed to generate barcode. Please try again.');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateBarcode = async (barcodeId) => {
    try {
      // Deactivate old barcode
      await supabase
        ?.from('member_barcodes')
        ?.update({ is_active: false })
        ?.eq('id', barcodeId);

      // Generate new barcode
      await handleGenerateBarcode();
    } catch (error) {
      console.error('Error regenerating barcode:', error);
      setError('Failed to regenerate barcode.');
    }
  };

  const handleDeactivateBarcode = async (barcodeId) => {
    try {
      const { error } = await supabase
        ?.from('member_barcodes')
        ?.update({ is_active: false })
        ?.eq('id', barcodeId);

      if (error) throw error;

      fetchAllBarcodes();
    } catch (error) {
      console.error('Error deactivating barcode:', error);
      setError('Failed to deactivate barcode.');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Member Barcode Generator
          </h1>
          <p className="text-muted-foreground">
            Generate and manage unique identification codes for gym members
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b border-border">
          <button
            onClick={() => setActiveTab('my-barcode')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'my-barcode' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="QrCode" size={18} className="inline mr-2" />
            My Barcode
          </button>
          {isStaffMember && (
            <>
              <button
                onClick={() => setActiveTab('generate')}
                className={`px-4 py-2 font-medium transition-colors ${
                  activeTab === 'generate' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name="Plus" size={18} className="inline mr-2" />
                Generate
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`px-4 py-2 font-medium transition-colors ${
                  activeTab === 'history' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name="History" size={18} className="inline mr-2" />
                History
              </button>
            </>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-destructive/10 border border-destructive rounded-lg">
            <p className="text-sm text-destructive flex items-center gap-2">
              <Icon name="AlertCircle" size={16} />
              {error}
            </p>
          </div>
        )}

        {/* Tab Content */}
        {activeTab === 'my-barcode' && (
          <div className="max-w-2xl mx-auto">
            <BarcodeDisplay
              barcode={userBarcode}
              loading={loading}
              onGenerate={() => handleGenerateBarcode()}
              onRegenerate={() => handleRegenerateBarcode(userBarcode?.id)}
            />
          </div>
        )}

        {activeTab === 'generate' && isStaffMember && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <BarcodeGenerationForm
              onGenerate={handleGenerateBarcode}
              loading={loading}
            />
            
            <div className="bg-card rounded-lg p-6 border border-border">
              <h2 className="text-xl font-bold mb-4">Bulk Generation</h2>
              <p className="text-muted-foreground mb-6">
                Generate barcodes for multiple members at once with batch processing.
              </p>
              <Button
                onClick={() => setShowBulkModal(true)}
                iconName="Users"
                className="w-full"
              >
                Start Bulk Generation
              </Button>
            </div>
          </div>
        )}

        {activeTab === 'history' && isStaffMember && (
          <BarcodeHistoryList
            barcodes={allBarcodes}
            onDeactivate={handleDeactivateBarcode}
            onRefresh={fetchAllBarcodes}
          />
        )}
      </div>

      {/* Bulk Generation Modal */}
      {showBulkModal && (
        <BulkGenerationModal
          onClose={() => setShowBulkModal(false)}
          onSuccess={() => {
            setShowBulkModal(false);
            fetchAllBarcodes();
          }}
        />
      )}
    </div>
  );
};

export default MemberBarcodeGenerator;